import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { Sparkles, RefreshCw, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export default function AffirmationsPage() {
  const { user, getAuthHeader, API } = useAuth();
  const { i18n } = useTranslation();
  const [affirmations, setAffirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    fetchAffirmations();
  }, [i18n.language]);

  const fetchAffirmations = async () => {
    setLoading(true);
    const lang = i18n.language === 'no' ? 'no' : 'en';
    try {
      const response = await axios.get(`${API}/affirmations?lang=${lang}`, { headers: getAuthHeader() });
      setAffirmations(response.data);
    } catch (error) {
      console.error('Failed to fetch affirmations:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      athlete: 'Athlete',
      golfer: 'Golfer',
      business_leader: 'Business Leader'
    };
    return labels[category] || 'Champion';
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="affirmations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif font-bold text-2xl md:text-3xl text-foreground">Daily Affirmations</h1>
            <p className="text-muted-foreground">
              Powerful mantras for {getCategoryLabel(user?.category)}s
            </p>
          </div>
          <Button
            onClick={fetchAffirmations}
            disabled={loading}
            variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/10 rounded-full"
            data-testid="refresh-affirmations-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Instructions */}
        <Card className="glass neon-border">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-full bg-primary/20">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-serif font-semibold text-lg text-foreground mb-2">How to Use Affirmations</h2>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Read each affirmation slowly and with intention
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Visualize yourself embodying the statement
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Repeat 3 times, especially before high-pressure moments
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Affirmations Grid */}
        <div className="grid gap-4">
          <AnimatePresence mode="wait">
            {loading ? (
              <div className="text-center py-12">
                <Sparkles className="h-8 w-8 text-primary mx-auto animate-pulse" />
                <p className="text-muted-foreground mt-4">Loading your affirmations...</p>
              </div>
            ) : (
              affirmations.map((affirmation, index) => (
                <motion.div
                  key={affirmation.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-card/50 border-white/5 hover:border-primary/30 transition-all duration-300 group">
                    <CardContent className="p-6 md:p-8">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-mono text-xs tracking-widest uppercase text-primary/60 mb-3">
                            Affirmation {index + 1}
                          </p>
                          <p className="font-serif text-xl md:text-2xl text-foreground leading-relaxed italic" data-testid={`affirmation-${index}`}>
                            "{affirmation.text}"
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyToClipboard(affirmation.text, index)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`copy-affirmation-${index}`}
                        >
                          {copiedIndex === index ? (
                            <Check className="h-4 w-4 text-secondary" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>

        {/* Practice Section */}
        <Card className="bg-card/30 border-white/5">
          <CardContent className="p-8 text-center">
            <h3 className="font-serif text-xl text-foreground mb-2">Daily Practice Reminder</h3>
            <p className="text-muted-foreground max-w-lg mx-auto">
              The best times to practice affirmations are in the morning upon waking, 
              before training or important events, and at night before sleep. 
              Consistency is key to rewiring your mindset.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
